/**
 * DashboardPage.jsx — admin overview with charts.
 * Pure SVG + CSS — no chart library needed.
 */
import { useEffect, useState } from "react";
import { adminGetStats } from "../../services/api";

// ── SVG chart primitives ───────────────────────────────────────────────────────

/** Multi-segment donut ring — e.g. user breakdown */
function DonutChart({ segments, size = 96, thickness = 13 }) {
  const total = segments.reduce((s, g) => s + (g.value || 0), 0) || 1;
  const cx = size / 2, cy = size / 2;
  const r  = cx - thickness / 2 - 1;
  const c  = 2 * Math.PI * r;
  let angle = -90;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#eef0eb" strokeWidth={thickness} />
      {segments.map((seg, i) => {
        if (!seg.value) return null;
        const arc = (seg.value / total) * c;
        const a   = angle;
        angle    += (seg.value / total) * 360;
        return (
          <circle key={i} cx={cx} cy={cy} r={r}
            fill="none" stroke={seg.color} strokeWidth={thickness}
            strokeDasharray={`${arc} ${c}`}
            transform={`rotate(${a}, ${cx}, ${cy})`} />
        );
      })}
      <text x={cx} y={cy - 5} textAnchor="middle" dominantBaseline="central"
        style={{ fontSize: size * 0.22, fontWeight: 800, fill: "#212529",
          fontFamily: "Nunito,sans-serif" }}>
        {total}
      </text>
      <text x={cx} y={cy + size * 0.18} textAnchor="middle"
        style={{ fontSize: size * 0.11, fill: "#adb5bd",
          fontFamily: "Nunito Sans,sans-serif" }}>
        total
      </text>
    </svg>
  );
}

/** Single-metric ring showing a percentage */
function RingProgress({ value, total, color, size = 66, thickness = 9 }) {
  const pct = total > 0 ? Math.min(1, value / total) : 0;
  const cx  = size / 2, cy = size / 2;
  const r   = cx - thickness / 2 - 1;
  const c   = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#eef0eb" strokeWidth={thickness} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={thickness}
        strokeDasharray={`${pct * c} ${c}`}
        transform={`rotate(-90, ${cx}, ${cy})`} strokeLinecap="round" />
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
        style={{ fontSize: size * 0.23, fontWeight: 700, fill: color,
          fontFamily: "Nunito,sans-serif" }}>
        {Math.round(pct * 100)}%
      </text>
    </svg>
  );
}

/** Horizontal ratio bar */
function FillBar({ value, total, color, height = 7 }) {
  const pct = total > 0 ? Math.min(100, Math.round((value / total) * 100)) : 0;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between",
        fontSize: 10, color: "#adb5bd", marginBottom: 3 }}>
        <span>{value} of {total}</span>
        <span style={{ color, fontWeight: 600 }}>{pct}%</span>
      </div>
      <div style={{ background: "#eef0eb", borderRadius: 4, height, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color,
          borderRadius: 4, transition: "width .7s ease" }} />
      </div>
    </div>
  );
}

/** Two-colour split bar for success / failure */
function SplitBar({ a, aColor, b, bColor }) {
  const total = (a + b) || 1;
  const pct   = Math.round((a / total) * 100);
  return (
    <div>
      <div style={{ height: 10, borderRadius: 5, overflow: "hidden", display: "flex" }}>
        <div style={{ width: `${pct}%`, background: aColor, transition: "width .7s ease" }} />
        <div style={{ flex: 1, background: bColor }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between",
        fontSize: 10, marginTop: 4, fontWeight: 600 }}>
        <span style={{ color: aColor }}>✓ {a} success</span>
        <span style={{ color: bColor }}>✕ {b} failed</span>
      </div>
    </div>
  );
}

/** Decorative mini bar-chart strip (static visual rhythm) */
function MiniBarStrip({ color, heights = [10,16,12,20,14,18,11,22,15,19,13,24] }) {
  const w = 160, h = 28;
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      {heights.map((bh, i) => (
        <rect key={i} x={i * (w / heights.length) + 1} y={h - bh}
          width={w / heights.length - 3} height={bh} rx={2}
          fill={color} opacity={0.15 + (i / heights.length) * 0.55} />
      ))}
    </svg>
  );
}

/** Document stack visual — n stacked rectangles (capped at 8) */
function DocStack({ count, color }) {
  const n   = Math.min(count || 0, 8);
  const w   = 160, h = 28;
  const bw  = 14, bh = 20, gap = 16;
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`}>
      {Array.from({ length: n }).map((_, i) => (
        <rect key={i} x={i * gap + 2} y={4} width={bw} height={bh}
          rx={2} fill={color}
          opacity={i === 0 ? 0.9 : Math.max(0.2, 0.85 - i * 0.1)} />
      ))}
      {n === 0 && (
        <text x={w / 2} y={h / 2 + 1} textAnchor="middle" dominantBaseline="central"
          style={{ fontSize: 10, fill: "#adb5bd" }}>no files yet</text>
      )}
    </svg>
  );
}

// ── Card shell ─────────────────────────────────────────────────────────────────

function Card({ label, accent, children }) {
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e9ecef",
      borderTop: `3px solid ${accent ?? "#dee2e6"}`,
      borderRadius: 12,
      padding: "1rem 1.1rem",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      boxShadow: "0 1px 3px rgba(0,0,0,.04)",
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#6c757d",
        textTransform: "uppercase", letterSpacing: ".06em" }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function Num({ v, color }) {
  return (
    <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1,
      color: color ?? "#212529", fontFamily: "'Nunito',sans-serif" }}>
      {v ?? "—"}
    </div>
  );
}

function SectionHead({ children }) {
  return (
    <h6 style={{ fontSize: 11, fontWeight: 600, color: "#adb5bd",
      textTransform: "uppercase", letterSpacing: ".08em",
      margin: "1.75rem 0 .75rem" }}>
      {children}
    </h6>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    adminGetStats()
      .then(setStats)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <style>{`
        .dash-4 { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; }
        .dash-3 { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; }
        @media (max-width: 860px) {
          .dash-4, .dash-3 { grid-template-columns:repeat(2,1fr); }
        }
        @media (max-width: 480px) {
          .dash-4, .dash-3 { grid-template-columns:1fr; }
        }
      `}</style>

      <div className="p-4" style={{ maxWidth: 1020 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: "#5C4B00",
          fontFamily: "'Nunito',sans-serif", marginBottom: 2 }}>Dashboard</h2>
        <div style={{ fontSize: 12, color: "#adb5bd", marginBottom: "0.5rem" }}>
          BeXyl DSS - platform overview
        </div>

        {error   && <div className="alert alert-danger py-2 mt-3" style={{ fontSize: 13 }}>{error}</div>}
        {loading && <div style={{ color: "#adb5bd", fontSize: 13, marginTop: "1rem" }}>Loading…</div>}

        {stats && (
          <>
            {/* ── Users ─────────────────────────────────────────────────────── */}
            <SectionHead>Users</SectionHead>
            <div className="dash-4">

              {/* Overview donut */}
              <Card label="All users" accent="#4B7518">
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <DonutChart segments={[
                    { value: stats.active_users,  color: "#3B6D11" },
                    { value: stats.pending_users, color: "#D4820A" },
                    { value: stats.blocked_users, color: "#A32D2D" },
                  ]} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                    {[
                      { label: "Active",  v: stats.active_users,  color: "#3B6D11" },
                      { label: "Pending", v: stats.pending_users, color: "#D4820A" },
                      { label: "Blocked", v: stats.blocked_users, color: "#A32D2D" },
                    ].map(({ label, v, color }) => (
                      <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 9, height: 9, borderRadius: 2,
                          background: color, flexShrink: 0 }} />
                        <span style={{ fontSize: 11, color: "#6c757d", minWidth: 44 }}>{label}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              {/* Active */}
              <Card label="Active users" accent="#3B6D11">
                <Num v={stats.active_users} color="#3B6D11" />
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <RingProgress value={stats.active_users}
                    total={stats.total_users} color="#3B6D11" />
                  <span style={{ fontSize: 11, color: "#6c757d" }}>of total users</span>
                </div>
              </Card>

              {/* Pending */}
              <Card label="Pending approval" accent="#D4820A">
                <Num v={stats.pending_users} color="#D4820A" />
                <FillBar value={stats.pending_users}
                  total={stats.total_users} color="#D4820A" />
                <span style={{ fontSize: 10, color: "#adb5bd" }}>awaiting approval</span>
              </Card>

              {/* Blocked */}
              <Card label="Blocked" accent="#A32D2D">
                <Num v={stats.blocked_users} color="#A32D2D" />
                <FillBar value={stats.blocked_users}
                  total={stats.total_users} color="#A32D2D" />
                <span style={{ fontSize: 10, color: "#adb5bd" }}>access suspended</span>
              </Card>
            </div>

            {/* ── Content ───────────────────────────────────────────────────── */}
            <SectionHead>Content</SectionHead>
            <div className="dash-4">

              {/* IPM schemes */}
              <Card label="IPM schemes" accent="#185FA5">
                <Num v={stats.total_schemes} color="#185FA5" />
                <FillBar value={stats.scored_schemes}
                  total={stats.total_schemes} color="#4B7518" />
                <span style={{ fontSize: 10, color: "#adb5bd" }}>
                  {stats.scored_schemes} scored of {stats.total_schemes} total
                </span>
              </Card>

              {/* Evaluated schemes — ring */}
              <Card label="Evaluated schemes" accent="#4B7518">
                <Num v={stats.scored_schemes} />
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <RingProgress value={stats.scored_schemes}
                    total={stats.total_schemes || 1} color="#4B7518" />
                  <span style={{ fontSize: 11, color: "#6c757d" }}>with expert score</span>
                </div>
              </Card>

              {/* Documents */}
              <Card label="Documents" accent="#775F46">
                <Num v={stats.total_documents} />
                <DocStack count={stats.total_documents} color="#775F46" />
                <span style={{ fontSize: 10, color: "#adb5bd" }}>reference files uploaded</span>
              </Card>

              {/* Searches */}
              <Card label="Wizard searches" accent="#3C3489">
                <Num v={stats.total_searches} color="#3C3489" />
                <MiniBarStrip color="#3C3489"
                  heights={[8,14,10,20,13,17,9,22,15,18,11,24]} />
                <span style={{ fontSize: 10, color: "#adb5bd" }}>queries submitted</span>
              </Card>
            </div>

            {/* ── Security ──────────────────────────────────────────────────── */}
            <SectionHead>Security</SectionHead>
            <div className="dash-3">

              {/* Total logins */}
              <Card label="Total logins" accent="#4B7518">
                <Num v={stats.total_logins} />
                <MiniBarStrip color="#4B7518"
                  heights={[10,14,11,16,13,18,15,20,17,22,19,24]} />
                <span style={{ fontSize: 10, color: "#adb5bd" }}>successful sign-ins</span>
              </Card>

              {/* Login success rate — split bar */}
              <Card label="Login success rate" accent="#3B6D11">
                {(() => {
                  const total = (stats.total_logins || 0) + (stats.failed_logins || 0);
                  const pct   = total > 0
                    ? Math.round((stats.total_logins / total) * 100) : null;
                  const col   = pct == null ? "#adb5bd"
                    : pct >= 90 ? "#3B6D11" : pct >= 70 ? "#D4820A" : "#A32D2D";
                  return (
                    <>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
                        <span style={{ fontSize: 30, fontWeight: 800, color: col,
                          fontFamily: "'Nunito',sans-serif", lineHeight: 1 }}>
                          {pct ?? "—"}
                        </span>
                        {pct != null && (
                          <span style={{ fontSize: 16, color: "#adb5bd" }}>%</span>
                        )}
                      </div>
                      <SplitBar
                        a={stats.total_logins}  aColor="#3B6D11"
                        b={stats.failed_logins} bColor="#A32D2D" />
                    </>
                  );
                })()}
              </Card>

              {/* Failed logins */}
              <Card label="Failed logins" accent="#A32D2D">
                <Num v={stats.failed_logins} color="#A32D2D" />
                {(() => {
                  const total = (stats.total_logins || 0) + (stats.failed_logins || 0);
                  return (
                    <>
                      <FillBar value={stats.failed_logins} total={total} color="#A32D2D" />
                      <span style={{ fontSize: 10, color: "#adb5bd" }}>
                        of all login attempts
                      </span>
                    </>
                  );
                })()}
              </Card>
            </div>
          </>
        )}
      </div>
    </>
  );
}
